import { RiskyDisclosureParagraph } from '@/components/common/DisclosureParagraph';
import FadeInBox from '@/components/common/FadeInBox';
import { FlexRow } from '@/components/common/Flex';
import { InfoButtonWithModal } from '@/components/common/InfoModal';
import { useToasts } from '@/components/common/Toasts';
import { Header, Text } from '@/components/common/Typography';
import { toDollars } from '@/util/currency';
import { useQuery } from '@apollo/client';
import { Center, FlexProps, Spinner } from '@chakra-ui/react';
import { assert } from '@common/errors';
import { calculateForecastDate } from '@common/forecasting';
import { FinancialEntitySnapshotsDocument } from '@common/graphql/generated';
import { getPartnerGoalAccount } from '@common/react/goals';
import { useCurrentUser } from '@common/react/user';
import { TOASTS } from '@common/toasts';
import {
    GoalAccountOnboardingStep,
    TransactionSchedulePlan,
} from '@common/types';
import { dt } from '@common/util/dateTime';
import { makeTickRange } from '@common/valueSeries';
import { useMemo } from 'react';
import { configByGoalType } from '../../../config';
import {
    getMultiplayerOnboardingPlanChartEntities,
    getSingleplayerOnboardingPlanChartEntities,
} from '../../../goalOnboardingChartHelper';
import { GoalsChartEntityParams } from '../../../GoalsChart';
import {
    calculateComplimentaryTransactionSchedulePlan,
    getAnnualReturnInfoForRiskScore,
    getGoalDataFromExistingGoal,
} from '../../../helpers';
import { useAccountSetupSteps } from '../../../useAccountSetupSteps';
import { useGoalDetailContext } from '../../GoalDetailContext';
import { useGoalOnboardingContext } from '../GoalOnboardingContext';
import GoalOnboardingLayout from '../GoalOnboardingLayout';
import ChartContainer from './ChartContainer';
import { getPotentialGainsOnTargetDate } from './helpers';
import InputsContainer from './InputsContainer';

export interface GoalOnboardingPlanProps extends FlexProps {
    onComplete: (step?: GoalAccountOnboardingStep) => void;
    onBack: () => void;
}

const GoalOnboardingPlan = ({
    onComplete,
    onBack,
    ...props
}: GoalOnboardingPlanProps) => {
    const { userInfo } = useCurrentUser();
    const { showSuccessToast } = useToasts();

    // TODO: Try to remove this `goalShallow` and just use `goal` from below,
    // but consider differences between filtered and unfiltered.
    const { currentRateInfo, goal: goalShallow } = useGoalDetailContext();
    const {
        goal,
        goalOnboardingData,
        updateGoalOnboardingData,
        partnerGoalAccountData,
        isUpdating,
        updateGoalOnboarding,
    } = useGoalOnboardingContext();
    const partnerGoalAccountId = getPartnerGoalAccount(goal)?.id;
    const { data: partnerGoalAccount } = useQuery(
        FinancialEntitySnapshotsDocument,
        partnerGoalAccountId
            ? {
                  variables: { id: partnerGoalAccountId },
                  fetchPolicy: 'no-cache',
              }
            : { skip: true }
    );
    const partnerGoalAccountSnapshots =
        partnerGoalAccount?.financialEntity?.snapshots ?? [];
    const isChartEntitiesLoading =
        partnerGoalAccountId && !partnerGoalAccountSnapshots;

    const isSharedGoal = goal.shareStatus === 'SHARED';
    const { handleNextStep } = useAccountSetupSteps({
        checkPartnership: goal.accountStructure !== 'PRIVATE',
        checkJointAccountConsent: goal.accountStructure === 'JOINT',
        onComplete: async (justCompletedIdv) => {
            await updateGoalOnboarding({
                params: {
                    info: {
                        initialDepositPlan:
                            goalOnboardingData.initialDepositPlan,
                        transactionSchedulePlan:
                            goalOnboardingData.transactionSchedulePlan,
                        lastCompletedStep: 'PLAN',
                    },
                },
            });
            let step: undefined | GoalAccountOnboardingStep;
            if (
                justCompletedIdv &&
                goalOnboardingData.investmentType !== 'HIGH_YIELD_CASH' &&
                (goal.targetDate || goal.type === 'GENERAL_INVESTING')
                // TODO: Also verify riskScoresByHorizon were actually synced
            ) {
                step = 'PORTFOLIO';
                // Delay by 1s to give time for transition to complete, and this
                // toast to appear after IDV success toast.
                setTimeout(() => {
                    showSuccessToast(TOASTS.recommendedRiskScoreUnlocked);
                }, 1000);
            }
            onComplete(step);
        },
    });

    const {
        riskScore,
        transactionSchedulePlan,
        initialDepositPlan,
        investmentType,
    } = goalOnboardingData;

    assert(investmentType, 'Missing investmentType');

    const goalConfig = configByGoalType[goal.type];
    const goalData = getGoalDataFromExistingGoal(goal);
    const targetDate = goalConfig.getTargetDate({
        goalData,
        userInfo,
        currentRateInfo,
    });

    const annualReturn = goalConfig.getAnnualReturn({
        investmentType: investmentType,
        riskScore: goalOnboardingData.riskScore,
        currentRateInfo,
    });

    const range = useMemo(() => {
        // If a target date exists for goal, use that + 50% buffer on the end so
        // the callout appears two-thirds from the left.
        // If no target date exists for goal, show 10 years.
        const rangeEnd = targetDate
            ? dt(targetDate).add(
                  Math.round(dt(targetDate).diff(dt(), 'month') * 0.5),
                  'month'
              )
            : dt().add(10, 'y');
        const rangeStart = dt().toDateStr();

        return makeTickRange(rangeStart, rangeEnd.toDateStr(), 'month');
    }, [targetDate]);

    const fundingType =
        goalConfig.group === 'HIGH_YIELD_CASH_ONLY'
            ? 'contribution'
            : 'investment';

    if (!goalShallow) {
        console.warn('Missing goalShallow');
        return <></>;
    }

    let partnerTransactionSchedulePlan: TransactionSchedulePlan | undefined;
    let forecastCompletionDate: string | undefined;
    let chartEntities: GoalsChartEntityParams[] = [];
    if (isSharedGoal) {
        const partnerStartAmount = partnerGoalAccountData?.startAmount ?? 0;
        const partnerAnnualReturn =
            partnerGoalAccountData?.annualReturn ?? annualReturn;
        partnerTransactionSchedulePlan =
            // If partner is already investing, using their current values
            partnerGoalAccountData?.transactionSchedulePlans?.at(0) ??
            // If target amount & date exist, calculate a plan to reach target
            (goal.targetAmount && targetDate
                ? calculateComplimentaryTransactionSchedulePlan({
                      existingPlan: {
                          startAmount: partnerStartAmount,
                          transactionSchedulePlan,
                          annualReturn: partnerAnnualReturn,
                      },
                      annualReturn,
                      startAmount: initialDepositPlan,
                      startDate: dt().toDateStr(),
                      targetAmount: goal.targetAmount,
                      targetDate,
                  })
                : // Otherwise, don't establish a partner plan
                  undefined);

        forecastCompletionDate = goal.targetAmount
            ? calculateForecastDate({
                  startDate: dt().toDateStr(),
                  targetAmount: goal.targetAmount,
                  targetDate: goal.targetDate,
                  plans: [
                      {
                          startAmount: initialDepositPlan,
                          annualGrowthRate: annualReturn,
                          transactionSchedulePlans: [transactionSchedulePlan],
                      },
                      {
                          startAmount: partnerStartAmount,
                          annualGrowthRate: partnerAnnualReturn,
                          transactionSchedulePlans:
                              partnerTransactionSchedulePlan
                                  ? [partnerTransactionSchedulePlan]
                                  : undefined,
                      },
                  ],
              })
            : undefined;
        chartEntities = partnerTransactionSchedulePlan
            ? getMultiplayerOnboardingPlanChartEntities({
                  goal: goalShallow,
                  targetDate,
                  annualReturn,
                  initialDepositPlan,
                  transactionSchedulePlan,
                  forecastCompletionDate,
                  range,
                  partnerGoalAccount: getPartnerGoalAccount(goalShallow),
                  partnerGoalAccountSnapshots,
                  partnerInitialDepositPlan: partnerStartAmount,
                  partnerTransactionSchedulePlan,
                  partnerAnnualReturn,
              })
            : getSingleplayerOnboardingPlanChartEntities({
                  goal: goalShallow,
                  targetDate,
                  annualReturn,
                  initialDepositPlan,
                  transactionSchedulePlan,
                  forecastCompletionDate,
                  range,
              });
    } else {
        forecastCompletionDate = goal.targetAmount
            ? calculateForecastDate({
                  startDate: dt().toDateStr(),
                  targetAmount: goal.targetAmount,
                  targetDate: goal.targetDate,
                  plans: [
                      {
                          startAmount: initialDepositPlan,
                          annualGrowthRate: annualReturn,
                          transactionSchedulePlans: [transactionSchedulePlan],
                      },
                  ],
              })
            : undefined;
        chartEntities = getSingleplayerOnboardingPlanChartEntities({
            goal: goalShallow,
            targetDate,
            annualReturn,
            initialDepositPlan,
            transactionSchedulePlan,
            forecastCompletionDate,
            range,
        });
    }

    // Calculate potential gains for the current user (excluding partner)
    const potentialGainsOnTargetDate = getPotentialGainsOnTargetDate({
        annualReturn: annualReturn,
        initialDepositPlan,
        targetDate,
        transactionSchedulePlans: [transactionSchedulePlan],
    });

    const onChangeInitialDepositPlan = (initialDepositPlan: number) => {
        updateGoalOnboardingData(
            {
                ...goalOnboardingData,
                initialDepositPlan,
            },
            { clampToTargetAmount: true }
        );
    };

    const onChangeTransactionSchedulePlan = (
        newPlan: TransactionSchedulePlan
    ) => {
        updateGoalOnboardingData({
            ...goalOnboardingData,
            transactionSchedulePlan: newPlan,
        });
    };

    const {
        annualReturnLabel,
        annualReturnLabelCapitalized,
        annualReturnStr,
        annualReturnDisclosure,
    } = getAnnualReturnInfoForRiskScore({
        investmentType,
        riskScore,
        currentRateInfo,
    });

    return (
        <GoalOnboardingLayout
            {...props}
            footer={{
                cta: {
                    onComplete: () => handleNextStep({}),
                    isSubmitting: isUpdating,
                },
                back: {
                    onBack,
                },
            }}
        >
            <FadeInBox>
                <Header mb={{ base: 6, '2xl': 8 }}>
                    {'Choose a starting amount to invest'}
                </Header>
                <InputsContainer
                    initialDepositPlan={initialDepositPlan}
                    onChangeInitialDepositPlan={onChangeInitialDepositPlan}
                    transactionSchedulePlan={transactionSchedulePlan}
                    onChangeTransactionSchedulePlan={
                        onChangeTransactionSchedulePlan
                    }
                    partnerTransactionSchedulePlan={
                        partnerTransactionSchedulePlan
                    }
                    mb={{ base: 6, '2xl': 8 }}
                />
                <FlexRow
                    mb={{ base: 6, '2xl': 8 }}
                    minH={220}
                    maxH="2xs"
                    position="relative"
                >
                    {isChartEntitiesLoading ? (
                        <Center w="full">
                            <Spinner />
                        </Center>
                    ) : (
                        <ChartContainer
                            chartEntities={chartEntities}
                            range={range}
                        />
                    )}
                </FlexRow>
                {targetDate && (
                    <Text mb={{ base: 6, '2xl': 8 }} color="textMuted">
                        With {annualReturnLabel} of {annualReturnStr}, this{' '}
                        {fundingType} plan would return{' '}
                        <Text fontWeight="semibold" color={goalConfig.color}>
                            {toDollars(potentialGainsOnTargetDate)}
                        </Text>{' '}
                        in potential gains after {dt(targetDate).fromNow(true)}.{' '}
                        <InfoButtonWithModal
                            buttonProps={{
                                h: 4,
                                w: 4,
                                onClickEventParams: {
                                    location: 'Goal Onboarding',
                                    step: 'PLAN',
                                },
                            }}
                            title={`${annualReturnLabelCapitalized} disclosures`}
                            modalSize="lg"
                        >
                            {annualReturnDisclosure}
                        </InfoButtonWithModal>
                    </Text>
                )}
                <RiskyDisclosureParagraph />
            </FadeInBox>
        </GoalOnboardingLayout>
    );
};

export default GoalOnboardingPlan;
